import { CaseExportWithCaseInfo, GetCaseExportsBulkZipPresignedUrlDocument } from "generated/graphql";
import { runGqlQuery } from "gqlClient";
import { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogTitle, FormControl, TextField } from "@mui/material";
import { format } from "date-fns";
import { triggerDownloadWithUrl } from "helpers/triggerDownload";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import { P } from "theme/typography";

interface BulkDownloadProps {
  exports: CaseExportWithCaseInfo[];
  open: boolean;
  onClose: () => void;
  lensName: string;
}

const BulkDownloadDialog = (props: BulkDownloadProps) => {
  const { exports, open, onClose, lensName } = props;
  const [filename, setFilename] = useState<string>(`Exports_${lensName}_${format(new Date(), "yyyyMMdd_HHmm")}.zip`);
  const [downloadPending, setDownloadPending] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const triggerBulkDownload = async () => {
    const exportIds = exports.map((e) => e.caseExport.id);
    setDownloadPending(true);
    runGqlQuery(GetCaseExportsBulkZipPresignedUrlDocument, {
      exportIds,
      fileName: filename.trim(),
    })
      .then((r) => {
        triggerDownloadWithUrl(r.getBulkZipPresignedUrl.url);
        setDownloadPending(false);
        onClose();
      })
      .catch(() => {
        enqueueSnackbar(`Failed to download exports`, {
          variant: "error",
        });
        setDownloadPending(false);
        onClose();
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Download Exports</DialogTitle>
      <Box sx={{ py: 2, px: 3, display: "flex", flexDirection: "column" }}>
        <P variant="base" sx={{ mb: 2 }}>
          Download a zip file containing {exports.length} {exports.length === 1 ? "export" : "exports"}
        </P>
        <FormControl fullWidth>
          <TextField
            disabled={downloadPending}
            fullWidth
            onChange={(evt) => setFilename(evt.target.value)}
            label="Save as  "
            placeholder="custom_name.zip"
            value={filename}
          />
        </FormControl>
      </Box>
      <DialogActions>
        <Button variant="text" sx={{ mr: 1 }} onClick={onClose}>
          Close
        </Button>
        <LoadingButton
          disabled={!filename.trim()}
          loading={downloadPending}
          loadingIndicator="Preparing..."
          variant="contained"
          onClick={triggerBulkDownload}>
          Download
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDownloadDialog;
